import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { 
  Briefcase, 
  ArrowRight,
  FileText,
  MessageSquare,
  Target,
  CheckCircle
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SEOPage = () => {
  const { role } = useParams();
  const [seoData, setSeoData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSEOContent = async () => {
      try {
        const response = await axios.get(`${API}/seo-content/${role}`);
        setSeoData(response.data);
      } catch (error) {
        console.error("Failed to fetch SEO content:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchSEOContent();
  }, [role]);

  const roleFormatted = role?.replace(/-/g, " ").replace(/\b\w/g, c => c.toUpperCase());

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="loading-spinner" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-white sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <Briefcase className="w-6 h-6 text-primary" />
              <span className="font-semibold text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Hireun
              </span>
            </Link>
            <Link to="/">
              <Button data-testid="get-started-btn">
                Get Started Free
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-16 bg-gradient-to-b from-accent/50 to-background">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Badge variant="secondary" className="mb-4">Interview Guide 2025</Badge>
          <h1 className="text-4xl sm:text-5xl font-bold mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {seoData?.h1 || `${roleFormatted} Interview Questions`}
          </h1>
          <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
            {seoData?.intro || `Prepare for your ${roleFormatted?.toLowerCase()} interview with personalized questions and expert-crafted answers.`}
          </p>
          <Link to="/">
            <Button size="lg" className="bg-primary hover:bg-primary/90" data-testid="cta-upload-cv">
              Upload Your CV for Personalized Prep
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Sections */}
        {seoData?.sections && (
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            {seoData.sections.map((section, i) => (
              <Card key={i} className="card-hover">
                <CardContent className="p-6">
                  {i === 0 && <Target className="w-8 h-8 text-primary mb-4" />}
                  {i === 1 && <MessageSquare className="w-8 h-8 text-primary mb-4" />}
                  {i === 2 && <FileText className="w-8 h-8 text-primary mb-4" />}
                  <h3 className="font-semibold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {section.title}
                  </h3>
                  <p className="text-sm text-muted-foreground">{section.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Sample Questions */}
        <Card className="mb-12">
          <CardHeader>
            <CardTitle style={{ fontFamily: 'Outfit, sans-serif' }}>
              Common {roleFormatted} Interview Questions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Accordion type="single" collapsible className="w-full">
              {seoData?.questions?.map((q, i) => (
                <AccordionItem key={i} value={`q-${i}`}>
                  <AccordionTrigger className="text-left">
                    <span className="flex items-center gap-3">
                      <span className="p-1.5 bg-primary/10 rounded text-xs font-semibold text-primary">
                        {q.type?.toUpperCase() || "Q"}
                      </span>
                      <span className="font-medium">{q.q}</span>
                    </span>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="pl-10 py-2">
                      <p className="text-muted-foreground">
                        Get a personalized answer to this question based on your CV and experience.
                      </p>
                      <Link to="/" className="inline-flex items-center text-primary text-sm mt-2 hover:underline">
                        Upload your CV for personalized answers
                        <ArrowRight className="w-3 h-3 ml-1" />
                      </Link>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </CardContent>
        </Card>

        {/* Why Use Our Tool */}
        <Card className="mb-12 bg-accent/30 border-primary/20">
          <CardContent className="p-8">
            <h2 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Why Use Hireun for {roleFormatted} Interviews?
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              {[
                "Answers based on YOUR actual experience",
                "Tailored to the specific job description",
                "STAR format for behavioral questions",
                "Ready-to-read introduction scripts",
                "10-15 likely questions with answers",
                "Free to use, no signup required"
              ].map((benefit, i) => (
                <div key={i} className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                  <span className="text-sm">{benefit}</span>
                </div>
              ))}
            </div>
            <div className="mt-8 text-center">
              <Link to="/">
                <Button size="lg" data-testid="cta-bottom">
                  Start Your Free Preparation
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>

        {/* Related Roles */}
        <div className="mb-12">
          <h3 className="text-xl font-semibold mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Interview Prep for Other Roles
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { slug: "software-engineer", name: "Software Engineer" },
              { slug: "data-analyst", name: "Data Analyst" },
              { slug: "product-manager", name: "Product Manager" },
              { slug: "consultant", name: "Consultant" }
            ].filter(r => r.slug !== role).map((r) => (
              <Link
                key={r.slug}
                to={`/interview-questions/${r.slug}`}
                className="p-4 bg-white rounded-lg border border-border hover:border-primary/50 transition-colors text-center text-sm font-medium"
              >
                {r.name}
              </Link>
            ))}
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-12 bg-foreground text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Briefcase className="w-5 h-5" />
                <span className="font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Hireun
                </span>
              </div>
              <p className="text-sm text-gray-400">
                AI-powered interview preparation tailored to your unique background.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Resources</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><Link to="/tell-me-about-yourself-interview" className="hover:text-white transition-colors">Tell Me About Yourself</Link></li>
                <li><Link to="/how-to-present-your-experience" className="hover:text-white transition-colors">Present Your Experience</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">By Role</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><Link to="/interview-questions/software-engineer" className="hover:text-white transition-colors">Software Engineer</Link></li>
                <li><Link to="/interview-questions/product-manager" className="hover:text-white transition-colors">Product Manager</Link></li>
                <li><Link to="/interview-questions/data-analyst" className="hover:text-white transition-colors">Data Analyst</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-sm text-gray-400">
            <p>&copy; 2025 Hireun. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default SEOPage;
